import type {
  ServiceCapability,
  ServiceConfig,
} from "@/components/services/ServiceLandingPage";

/** Default capability column for service landings that omit `capabilities` on their config. */
export const SERVICE_LANDING_DEFAULT_CAPABILITIES: ServiceCapability[] = [
  {
    title: "Connected software for real-world devices.",
    description:
      "We build modern, responsive software that interfaces with physical hardware devices and the systems behind them. From device controls and telemetry dashboards to operator workflows, we design for performance, accessibility, maintainability, and long-term reliability.",
  },
  {
    title: "Automation and data integration.",
    description:
      "We integrate automation and data pipelines to streamline workflows and reduce manual effort. Our systems connect disparate tools and platforms to create efficient in-house processes.",
  },
  {
    title: "AI where it makes sense.",
    description:
      "We integrate AI and machine learning where they meaningfully improve outcomes. Our approach focuses on practical applications that enhance human judgment and decision-making, not replace it.",
  },
];

/** Shared full connected positioning used across service surfaces. */
export const CONNECTED_SYSTEMS_FULL_TAGLINE =
  "Systems that connect, adapt, and improve over time. We connect modern user interfaces, automation, data, and AI where they meaningfully improve outcomes.";

/** Shared full growth positioning used across all service surfaces. */
export const GROWTH_SERVICE_FULL_TAGLINE =
  "Marketing sites that do way more than a landing page. Sites that integrate with your business tools, build trust with your audience, and scale with your growth.";

/** Shared full medical positioning used across service surfaces. */
export const MEDICAL_SYSTEMS_FULL_TAGLINE =
  "Connected systems for environments where mistakes matter. We design and build medical and regulated software where reliability, auditability, security, and long-term support are non-negotiable.";

/**
 * Main Copy section on `/connected` (textured two-column block below the work examples).
 * Keep this generic “Connected & Intelligent Systems” positioning only.
 * Do not put ChargerHelp case study copy here.
 */
export const CONNECTED_MAIN_COPY_HEADING =
  "We build connected software that bridges digital products with physical devices, operations, and real-world workflows.";

export const CONNECTED_MAIN_COPY_PARAGRAPHS: [string, string] = [
  "This work often supports teams building software that needs to control, monitor, or respond to physical hardware devices, while still delivering a modern product experience. When systems need to connect and adapt, we build the technical foundation that makes it possible. ",
  "Whether it’s integrating devices with your existing tools, creating new operational workflows, or adding intelligent automation where it meaningfully improves outcomes, we focus on solutions that are maintainable, secure, and built to evolve with your needs.",
];

export const MEDICAL_SERVICE_TAGLINE =
  "We design and build medical and regulated software where reliability, auditability, security, and long-term support are non-negotiable. Because excellence in medical device systems matters.";

export const MEDICAL_SERVICE_CONFIG: ServiceConfig = {
  id: "medical",
  title: "Software for Medical & Regulated Systems",
  heroTitle: "Software for Medical & Regulated Systems",
  tagline: MEDICAL_SERVICE_TAGLINE,
  description:
    "Developing software for medical and wearable devices is tricky. It requires a deep understanding of the capabilities of the hardware. Knowledge of how to handle the interplay between the hardware, the software, and the cloud. Nuances around battery life, compute, latency, offline. Perhaps most importantly, it requires an understanding of the documentation, processes and procedures necessary for regulatory compliance and FDA certification. You don’t want to wing it. You want to partner with a firm who’s been there before.",
  mainCopyHeading:
    "Developing software for medical and wearable devices is tricky.",
  mainCopy: [
    "It requires a deep understanding of the capabilities of the hardware. Knowledge of how to handle the interplay between the hardware, the software, and the cloud. Nuances around battery life, compute, latency, offline.",
    "Perhaps most importantly, it requires an understanding of the documentation, processes and procedures necessary for regulatory compliance and FDA certification. You don’t want to wing it. You want to partner with a firm who’s been there before. Township is that firm.",
  ],
  capabilities: [
    {
      title: "We understand hardware.",
      description:
        "Our software engineers are familiar with the capabilities (and limitations) of medical hardware. We work side-by-side with the hardware team to rigorously test and design solutions that balance performance and reliability.",
    },
    {
      title: "Future proof your solution.",
      description:
        "It’s easy to ship software. Hardware is much harder. Even firmware has its risks. Township helps clients anticipate future needs so they ship the right product the first time.",
    },
    {
      title: "FDA Compliance.",
      description:
        "Township provides detailed documentation and support to help you meet stringent FDA regulations, reducing the risk of compliance issues and time delays.",
    },
  ],
  icpContext: {
    roles: "VP Engineering, CTO, Clinical Systems Lead",
    context:
      "Operating in regulated environments with scrutiny, audits, and long-term risk.",
    needs: "Reliability, auditability, security, correctness.",
    coreFear: "Picking a partner who treats seriousness as optional.",
  },
  workCategory: "medical-devices",
  workItemIds: ["alcor-ised", "alcor-pro", "osteoboost"],
  workPageHref: "/work?category=medical-devices",
  workPageTitle: "See All Medical Work",
  ndaMessage: {
    title: "Beyond what we publish here",
    description:
      "Regulated programs often include confidentiality obligations we treat as non-negotiable: we do not use those engagements as public marketing material. When your process requires it, we can align on appropriate references and artifacts in a private conversation—structured for your diligence, and respectful of other clients’ boundaries.",
    ctaHref:
      "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0ToqyTsi9DDratVDfTBayCYGdIYbhNKJ0B-bR9suJOZjlOJwVohgQH9E1odhGOtvwtplwgL4Cp",
    ctaText: "Schedule a Call",
  },
  otherServices: [
    {
      title: "Growth Systems",
      tagline: GROWTH_SERVICE_FULL_TAGLINE,
      href: "/growth",
      hoverColor: "brand-pink",
    },
    {
      title: "Connected & Intelligent Systems",
      tagline: CONNECTED_SYSTEMS_FULL_TAGLINE,
      href: "/connected",
      hoverColor: "brand-yellow",
    },
  ],
};

export const GROWTH_SERVICE_CONFIG: ServiceConfig = {
  id: "growth",
  title: "Growth Systems",
  heroTitle: "Marketing Sites That Grow and Evolve With You",
  tagline: GROWTH_SERVICE_FULL_TAGLINE,
  description:
    "Building digital experiences that move people. We collaborate with ambitious teams to craft marketing sites that are actually apps — connected systems that integrate with your business tools, support real workflows, and scale with your growth.",
  mainCopyHeading: "A good marketing site IS an app.",
  mainCopy: [
    "It connects to your CRM, syncs with your content systems, and supports real workflows for your team. The question isn’t whether you need a marketing site or an app — it’s how to build both as one connected system. When your site integrates with your tools and workflows, it becomes a real part of how you operate, not just a brochure that sits separate from everything else.",
    "We often work with teams who started with a simple site and realized it needs to do more. Your marketing is your front door. Invite your audience in, share your story, and stay in touch. Whether it’s a marketing site, design system, or content structure, we build systems that are meant to grow and evolve — without requiring a complete rebuild when you need to grow, integrate tools, or handle real traffic.",
  ],
  capabilities: [
    {
      title: "Tell your story effectively.",
      description:
        "You have a story to tell, but there’s a lot of noise out there. We use design and implementation to help you communicate your message clearly and cut through the clutter.",
    },
    {
      title: "Connect the pieces. Built to grow.",
      description:
        "The web is a complicated place. To get your digital machine up and running, many separate tools need to be connected together. We build technical foundations that support real growth — sites designed to handle increased traffic, new features, and evolving requirements without requiring a complete rebuild. We connect the pieces and build to iterate.",
    },
    {
      title: "Made for everyone.",
      description:
        "Township is strongly committed to creating accessible web experiences.",
      descriptionLink: {
        href: "/accessibility",
        label: "Learn more on our accessibility page",
      },
    },
  ],
  icpContext: {
    roles: "CMO, Head of Growth, Founder",
    context: "Needs a site that can evolve into a system.",
    needs:
      "Design systems, content structure, integration — not a campaign page.",
    coreFear: "Buying a site that collapses under real growth.",
  },
  workCategory: "digital-experiences",
  workItemIds: ["anxious-generation", "good-inside", "safdie-architects"],
  workPageHref: "/work?category=digital-experiences",
  workPageTitle: "See All Growth Work",
  otherServices: [
    {
      title: "Medical & Regulated Systems",
      tagline: MEDICAL_SYSTEMS_FULL_TAGLINE,
      href: "/medical",
      hoverColor: "brand-teal",
    },
    {
      title: "Connected & Intelligent Systems",
      tagline: CONNECTED_SYSTEMS_FULL_TAGLINE,
      href: "/connected",
      hoverColor: "brand-yellow",
    },
  ],
};

export const CONNECTED_SERVICE_CONFIG: ServiceConfig = {
  id: "connected",
  title: "Connected & Intelligent Systems",
  heroTitle: "Connected & Intelligent Systems",
  tagline: CONNECTED_SYSTEMS_FULL_TAGLINE,
  description:
    "Township designs and builds connected systems that integrate with your tools and workflows, with the right use of data and AI where it matters.",
  mainCopyHeading: CONNECTED_MAIN_COPY_HEADING,
  mainCopy: [...CONNECTED_MAIN_COPY_PARAGRAPHS],
  /** ChargerHelp featured first; same long copy as work item `spotlightCopy` on the large card. */
  workItemIds: ["chargerhelp-empwr-mobile", "fluidlogic", "feast-and-fettle"],
  capabilities: [...SERVICE_LANDING_DEFAULT_CAPABILITIES],
  icpContext: {
    roles: "CTO, VP Engineering, Product Lead",
    context: "Disparate tools, vendors, and teams.",
    needs: "Integration, coherence, governance without bureaucracy.",
    coreFear: "Fragmentation getting worse, not better.",
  },
  workCategory: "connected-systems",
  workPageHref: "/work?category=connected-systems",
  workPageTitle: "See All Connected Work",
  otherServices: [
    {
      title: "Medical & Regulated Systems",
      tagline: MEDICAL_SYSTEMS_FULL_TAGLINE,
      href: "/medical",
      hoverColor: "brand-teal",
    },
    {
      title: "Growth Systems",
      tagline: GROWTH_SERVICE_FULL_TAGLINE,
      href: "/growth",
      hoverColor: "brand-pink",
    },
  ],
};
